import React, { useState, useEffect } from 'react';
import { Play, Pause, Square, ExternalLink } from 'lucide-react';
import mediaPlayerService from '../services/MediaPlayerService';

const CompactMediaPlayer = ({ 
  sheetId, 
  mediaUrl, 
  size = 'small',
  className = ''
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    // Subscribe to playback state changes for this sheet
    const unsubscribe = mediaPlayerService.subscribe(sheetId, (state) => {
      console.log('CompactMediaPlayer received state:', state, 'for sheet:', sheetId);
      
      switch (state) {
        case 'playing':
          setIsPlaying(true);
          setIsLoading(false);
          break;
        case 'paused':
        case 'stopped':
        case 'error':
          setIsPlaying(false);
          setIsLoading(false);
          break;
        case 'ready':
          setIsLoading(false);
          setIsPlaying(false);
          break;
      }
    });
    
    // Check if there's already a player for this sheet that's playing
    if (mediaPlayerService.currentSheetId === sheetId) {
      setIsPlaying(mediaPlayerService.isSheetPlaying(sheetId));
    }
    
    return unsubscribe;
  }, [sheetId]);
  
  const handlePlayPause = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!mediaUrl) return;
    
    console.log('handlePlayPause clicked for sheet:', sheetId, 'isPlaying:', isPlaying);
    
    // Desktop behavior
    try {
      if (isPlaying) {
        console.log('Pausing player for sheet:', sheetId);
        mediaPlayerService.pause();
      } else {
        console.log('Playing/starting player for sheet:', sheetId);
        
        // Check if player exists for this sheet but just needs to be played
        if (mediaPlayerService.currentSheetId === sheetId && mediaPlayerService.youtubePlayer) {
          console.log('Player exists, attempting direct play');
          try {
            mediaPlayerService.youtubePlayer.playVideo();
          } catch (error) {
            console.log('Direct play failed, falling back to full initialization');
            setIsLoading(true);
            await mediaPlayerService.play(sheetId, mediaUrl);
          }
        } else {
          // Set loading state when starting to initialize
          setIsLoading(true);
          // This will initialize the player if not already done
          await mediaPlayerService.play(sheetId, mediaUrl);
        }
      }
    } catch (error) {
      console.error('Error in handlePlayPause:', error);
      setIsLoading(false);
    }
  };
  
  const handleStop = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    await mediaPlayerService.stop();
  };
  
  const handleOpenLink = (e) => {
    e.stopPropagation();
    e.preventDefault();
    window.open(mediaUrl, '_blank');
  };
  
  // Show player immediately if there's a mediaUrl - no waiting for ready state
  if (!mediaUrl) return null;
  
  const sizeClasses = {
    small: {
      icon: 'w-3 h-3',
      button: 'p-1',
      gap: 'gap-1'
    },
    medium: {
      icon: 'w-4 h-4',
      button: 'p-1.5',
      gap: 'gap-1.5'
    }
  };
  
  const { icon: iconSize, button: buttonPadding, gap } = sizeClasses[size] || sizeClasses.small;
  
  const getButtonStyles = (isActive = false) => {
    const baseStyles = `${buttonPadding} rounded transition-all duration-200`;
    
    // No background color difference for active state - icon color handles the visual feedback
    return `${baseStyles} text-gray-600 hover:text-gray-900 hover:bg-gray-100`;
  };
  
  return (
    <div 
      className={`flex items-center ${gap} ${className}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Play/Pause Button */}
      <button
        onClick={handlePlayPause}
        className={getButtonStyles(isPlaying)}
        title={isPlaying ? 'Pause' : isLoading ? 'Loading...' : 'Play'}
      >
        {isPlaying ? (
          <Pause className={`${iconSize} text-blue-600`} fill="currentColor" />
        ) : (
          <Play className={`${iconSize} ${isLoading ? 'animate-pulse-icon' : ''}`} />
        )}
      </button>
      
      {/* Stop Button */}
      <button
        onClick={handleStop}
        className={getButtonStyles()}
        title="Stop"
      >
        <Square className={iconSize} />
      </button>
      
      {/* YouTube Link Button */}
      <button
        onClick={handleOpenLink}
        className={getButtonStyles()}
        title="Open in YouTube"
      >
        <ExternalLink className={iconSize} />
      </button>
    </div>
  );
};

export default CompactMediaPlayer;